// Trading State Management with Zustand
// Enhanced with position lifecycle tracking, intents, and activity feed

import { create } from 'zustand';
import { subscribeWithSelector, persist } from 'zustand/middleware';
import { killSwitchApi } from '../lib/api.tsx';
import type { Order, Signal, Ticker, Position } from '../types/api';

/**
 * Activity event types for the real-time activity feed
 */
export type ActivityEventType = 
  | 'intent_created' 
  | 'intent_approved' 
  | 'intent_rejected'
  | 'order_filled' 
  | 'position_open' 
  | 'position_close'
  | 'position_status_change';

/**
 * Activity event for the real-time activity feed
 */
export interface ActivityEvent {
  id: string;
  type: ActivityEventType;
  symbol: string;
  message: string;
  timestamp: string;
  details?: {
    intentId?: string;
    orderId?: string;
    positionId?: string;
    symbol?: string;
    side?: 'long' | 'short';
    size?: number;
    price?: number;
    pnl?: number;
    status?: string;
    reason?: string;
    acknowledged?: boolean;
  };
}

/**
 * Intent representing a trade intent that hasn't yet become a position
 */
export interface PendingIntent {
  id: string;
  symbol: string;
  side: 'buy' | 'sell';
  size?: number;
  status: 'pending' | 'approved' | 'rejected' | 'executing';
  confidence?: number;
  signalType?: string;
  rationale?: string;
  createdAt: string;
  updatedAt: string;
}

interface TradingState {
  // Watchlist
  watchlist: string[];
  addToWatchlist: (symbol: string) => void;
  removeFromWatchlist: (symbol: string) => void;
  
  // Selected symbol
  selectedSymbol: string | null;
  setSelectedSymbol: (symbol: string | null) => void;
  
  // Active signals
  activeSignals: Signal[];
  setActiveSignals: (signals: Signal[]) => void;
  
  // Tickers (real-time prices)
  tickers: Map<string, Ticker>;
  updateTicker: (symbol: string, ticker: Ticker) => void;
  getTicker: (symbol: string) => Ticker | undefined;
  
  // Kill switch
  killSwitchActive: boolean;
  syncKillSwitchState: (active: boolean) => void;
  activateKillSwitch: () => Promise<void>;
  deactivateKillSwitch: () => Promise<void>;
  
  // Order entry
  orderEntryOpen: boolean;
  setOrderEntryOpen: (open: boolean) => void;
  pendingOrder: Partial<Order> | null;
  setPendingOrder: (order: Partial<Order> | null) => void;
  clearPendingOrder: () => void;
  
  // ===== Position Lifecycle Tracking =====
  
  /**
   * Open positions (pending_fill, open, partially_filled, closing)
   */
  positions: Position[];
  
  /**
   * Closed positions history
   */
  closedPositions: Position[];
  
  /**
   * Pending intents that haven't yet become positions
   */
  pendingIntents: PendingIntent[];
  
  /**
   * Recent activity events for the activity feed
   */
  recentActivity: ActivityEvent[];
  
  /**
   * Maximum number of activity events to keep
   */
  maxActivityEvents: number;
  
  // ===== Position Actions =====
  
  /**
   * Set all open positions
   */
  setPositions: (positions: Position[]) => void;
  
  /**
   * Update a single position by ID
   */
  updatePosition: (positionId: string, updates: Partial<Position>) => void;
  
  /**
   * Add a new position (when position:open event received)
   */
  addPosition: (position: Position) => void;
  
  /**
   * Move a position to closed positions (when position:close event received)
   */
  closePosition: (positionId: string, exitReason?: string, closedAt?: string) => void;
  
  /**
   * Set closed positions history
   */
  setClosedPositions: (positions: Position[]) => void;
  
  /**
   * Add a closed position to history
   */
  addClosedPosition: (position: Position) => void;
  
  // ===== Intent Actions =====
  
  /**
   * Set all pending intents
   */
  setPendingIntents: (intents: PendingIntent[]) => void;
  
  /**
   * Add a new pending intent
   */
  addPendingIntent: (intent: PendingIntent) => void;
  
  /**
   * Update an intent's status
   */
  updateIntentStatus: (intentId: string, status: PendingIntent['status']) => void;
  
  /**
   * Remove an intent (when it becomes a position or is rejected)
   */
  removePendingIntent: (intentId: string) => void;
  
  // ===== Activity Actions =====
  
  /**
   * Add an activity event to the feed
   */
  addActivity: (event: ActivityEvent) => void;
  
  /**
   * Set all activity events
   */
  setActivity: (events: ActivityEvent[]) => void;
  
  /**
   * Clear all activity events
   */
  clearActivity: () => void;
  
  /**
   * Acknowledge an activity event
   */
  acknowledgeActivity: (eventId: string) => void;
}

export const useTradingStore = create<TradingState>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        // Watchlist
        watchlist: ['BTC/USDT', 'ETH/USDT', 'SOL/USDT'],
        
        addToWatchlist: (symbol) =>
          set((state) => ({
            watchlist: state.watchlist.includes(symbol)
              ? state.watchlist
              : [...state.watchlist, symbol],
          })),
        
        removeFromWatchlist: (symbol) =>
          set((state) => ({
            watchlist: state.watchlist.filter((s) => s !== symbol),
          })),
        
        // Selected symbol
        selectedSymbol: null,
        setSelectedSymbol: (symbol) => set({ selectedSymbol: symbol }),
        
        // Active signals
        activeSignals: [],
        setActiveSignals: (signals) => set({ activeSignals: signals }),
        
        // Tickers
        tickers: new Map(),
        
        updateTicker: (symbol, ticker) =>
          set((state) => {
            const newTickers = new Map(state.tickers);
            newTickers.set(symbol, ticker);
            return { tickers: newTickers };
          }),
        
        getTicker: (symbol) => get().tickers.get(symbol),
        
        // Kill switch
        killSwitchActive: false,
        syncKillSwitchState: (active) =>
          set({
            killSwitchActive: active,
            ...(active
              ? {
                  pendingOrder: null,
                  orderEntryOpen: false,
                }
              : {}),
          }),
        activateKillSwitch: async () => {
          await killSwitchApi.activate();
          get().syncKillSwitchState(true);
        },
        deactivateKillSwitch: async () => {
          await killSwitchApi.deactivate();
          get().syncKillSwitchState(false);
        },
        
        // Order entry
        orderEntryOpen: false,
        setOrderEntryOpen: (open) => set({ orderEntryOpen: open }),
        pendingOrder: null,
        setPendingOrder: (order) => set({ pendingOrder: order, orderEntryOpen: true }),
        clearPendingOrder: () => set({ pendingOrder: null, orderEntryOpen: false }),
        
        // ===== Position Lifecycle State =====
        positions: [],
        closedPositions: [],
        pendingIntents: [],
        recentActivity: [],
        maxActivityEvents: 100,
        
        // ===== Position Actions =====
        
        setPositions: (positions) => set({ positions }),
        
        updatePosition: (positionId, updates) =>
          set((state) => ({
            positions: state.positions.map((p) =>
              p.id === positionId ? { ...p, ...updates } : p
            ),
          })),
        
        addPosition: (position) =>
          set((state) => {
            // Check if position already exists
            const exists = state.positions.some((p) => p.id === position.id);
            if (exists) {
              // Update existing position
              return {
                positions: state.positions.map((p) =>
                  p.id === position.id ? position : p
                ),
              };
            }
            // Add new position
            return {
              positions: [position, ...state.positions],
            };
          }),
        
        closePosition: (positionId, exitReason, closedAt) =>
          set((state) => {
            const position = state.positions.find((p) => p.id === positionId);
            if (!position) return state;
            
            const closedPosition: Position = {
              ...position,
              status: 'closed',
              exitReason: exitReason || position.exitReason,
              closedAt: closedAt || new Date().toISOString(),
            };
            
            return {
              positions: state.positions.filter((p) => p.id !== positionId),
              closedPositions: [closedPosition, ...state.closedPositions].slice(0, 200),
            };
          }),
        
        setClosedPositions: (positions) => set({ closedPositions: positions }),
        
        addClosedPosition: (position) =>
          set((state) => ({
            closedPositions: [position, ...state.closedPositions].slice(0, 200),
          })),
        
        // ===== Intent Actions =====
        
        setPendingIntents: (intents) => set({ pendingIntents: intents }),
        
        addPendingIntent: (intent) =>
          set((state) => {
            const exists = state.pendingIntents.some((i) => i.id === intent.id);
            if (exists) {
              return {
                pendingIntents: state.pendingIntents.map((i) =>
                  i.id === intent.id ? intent : i
                ),
              };
            }
            return {
              pendingIntents: [intent, ...state.pendingIntents],
            };
          }),
        
        updateIntentStatus: (intentId, status) =>
          set((state) => ({
            pendingIntents: state.pendingIntents.map((i) =>
              i.id === intentId ? { ...i, status, updatedAt: new Date().toISOString() } : i
            ),
          })),
        
        removePendingIntent: (intentId) =>
          set((state) => ({
            pendingIntents: state.pendingIntents.filter((i) => i.id !== intentId),
          })),
        
        // ===== Activity Actions =====
        
        addActivity: (event) =>
          set((state) => ({
            recentActivity: [event, ...state.recentActivity].slice(0, state.maxActivityEvents),
          })),
        
        setActivity: (events) => set({ recentActivity: events }),
        
        clearActivity: () => set({ recentActivity: [] }),
        
        acknowledgeActivity: (eventId) =>
          set((state) => ({
            recentActivity: state.recentActivity.map((e) =>
              e.id === eventId ? { ...e, details: { ...e.details, acknowledged: true } } : e
            ),
          })),
      }),
      {
        name: 'trading-storage',
        partialize: (state) => ({
          watchlist: state.watchlist,
          selectedSymbol: state.selectedSymbol,
          killSwitchActive: state.killSwitchActive,
          // Persist closed positions for history
          closedPositions: state.closedPositions.slice(0, 50),
        }),
      }
    )
  )
);

// Selector hooks
export const useWatchlist = () => useTradingStore((state) => state.watchlist);
export const useSelectedSymbol = () => useTradingStore((state) => state.selectedSymbol);
export const useActiveSignals = () => useTradingStore((state) => state.activeSignals);
export const useKillSwitchActive = () => useTradingStore((state) => state.killSwitchActive);

// Position lifecycle selectors
export const usePositions = () => useTradingStore((state) => state.positions);
export const useClosedPositions = () => useTradingStore((state) => state.closedPositions);
export const usePendingIntents = () => useTradingStore((state) => state.pendingIntents);
export const useRecentActivity = () => useTradingStore((state) => state.recentActivity);

// Derived selectors
export const useOpenPositions = () => 
  useTradingStore((state) => 
    state.positions.filter((p) => p.status === 'open' || p.status === 'pending_fill' || p.status === 'partially_filled')
  );

export const useClosingPositions = () => 
  useTradingStore((state) => 
    state.positions.filter((p) => p.status === 'closing')
  );

export const usePositionById = (id: string) => 
  useTradingStore((state) => 
    state.positions.find((p) => p.id === id) || state.closedPositions.find((p) => p.id === id)
  );
